export const BENCHMARK_BOOKING_TYPE_CREATE_PATH =
  '/pga-coach/bookings/booking-types/new'
export const BENCHMARK_GROUP_EVENT_CREATE_PATH =
  '/pga-coach/bookings/events/new'
export const BENCHMARK_OFFERINGS_PATH = '/pga-coach/bookings/offerings'
export const BENCHMARK_LEADS_AUTO_REPLY_PATH =
  '/pga-coach/settings/leads-auto-reply'
export const BENCHMARK_LEADS_PATH = '/pga-coach/leads'
export const BENCHMARK_AVAILABILITY_PATH = '/pga-coach/settings/availability'

export const BENCHMARK_METRICS = [
  {
    id: 'first-to-second-conversion',
    label: 'First-to-second conversion',
    description: 'Students who book a second lesson after their first',
    calculationDescription:
      'Percent of first-time students who book a second lesson',
    coachValue: 46,
    medianValue: 52,
    top10Value: 71,
    gettingStartedValue: 25,
    unit: 'percent',
    higherIsBetter: true,
    insightBody:
      'Just under half of your first-time students come back for a second lesson. Closing the second booking in-person or within 24 hours is the biggest lever.',
    insightTip:
      'What top coaches do: Offer two specific times at the end of every first lesson and send a follow-up text within 24 hours.',
    recommendedSteps: [
      {
        id: 'lesson-pack',
        title: 'Create a lesson pack',
        description:
          'Set up a multi-lesson package students can purchase upfront and schedule over time.',
        icon: 'LibraryBooks',
        actionPath: BENCHMARK_BOOKING_TYPE_CREATE_PATH,
      },
    ],
  },
  {
    id: 'rebooking-rate',
    label: 'Rebooking rate',
    description: 'Active students who booked again in the last 60 days',
    calculationDescription:
      'Percent of active students who booked again in the last 60 days',
    coachValue: 68,
    medianValue: 61,
    top10Value: 78,
    gettingStartedValue: 40,
    unit: 'percent',
    higherIsBetter: true,
    insightBody:
      'Your returning students are loyal. Protecting that habit with recurring options and lesson packs will keep them on your calendar.',
    insightTip:
      'What top coaches do: Offer recurring weekly slots and a 10-lesson pack to the students who book most often.',
    recommendedSteps: [
      {
        id: 'lesson-pack',
        title: 'Create a lesson pack',
        description:
          'Offer multi-lesson packages so returning students can buy and book their next block of lessons.',
        icon: 'LibraryBooks',
        actionPath: BENCHMARK_BOOKING_TYPE_CREATE_PATH,
      },
      {
        id: 'recurring-availability',
        title: 'Set recurring availability',
        description:
          'Add weekly hours in availability settings so students can book standing lesson times.',
        icon: 'EventAvailable',
        actionPath: BENCHMARK_AVAILABILITY_PATH,
      },
    ],
  },
  {
    id: 'response-time',
    label: 'Response time',
    description: 'Median time to reply to a new lead',
    coachValue: 9,
    medianValue: 6,
    top10Value: 1,
    gettingStartedValue: 12,
    unit: 'hours',
    higherIsBetter: false,
    insightBody:
      'Leads who wait 9 hours are about half as likely to convert. Faster replies keep golfers in the mindset that made them reach out.',
    insightTip:
      'What top coaches do: Reply within 30 minutes with one question and two times to connect.',
    recommendedSteps: [
      {
        id: 'leads-auto-reply',
        title: 'Enable leads auto-reply',
        description:
          'Send an automatic response when a new inquiry arrives so golfers hear from you right away.',
        icon: 'AutoResponders',
        actionPath: BENCHMARK_LEADS_AUTO_REPLY_PATH,
      },
      {
        id: 'mark-off-platform-contacts',
        title: 'Mark off-platform contacts on your lead list',
        description:
          'When you reach out by phone, text, or email, mark the lead as contacted on your lead list so response time stays accurate.',
        icon: 'PlaylistAddCheck',
        actionPath: BENCHMARK_LEADS_PATH,
      },
    ],
  },
  {
    id: 'booking-offering-mix',
    label: 'Booking / offering mix',
    description: 'Share of bookings from group events and clinics',
    coachValue: 24,
    medianValue: 33,
    top10Value: 45,
    gettingStartedValue: 10,
    unit: 'percent',
    higherIsBetter: true,
    insightBody:
      'You are heavily weighted toward private lessons. Adding one weekly group offering can lift utilization without adding hours.',
    insightTip:
      'What top coaches do: Launch a weekly group clinic and a seasonal junior series alongside private lessons.',
    recommendedSteps: [
      {
        id: 'group-clinic',
        title: 'Create a group clinic',
        description:
          'Set up a group event with sessions, pricing, and registration for golfers to join.',
        icon: 'Group',
        actionPath: BENCHMARK_GROUP_EVENT_CREATE_PATH,
      },
    ],
  },
  {
    id: 'pricing',
    label: 'Pricing',
    description: 'Average private lesson rate on PGA Coach',
    coachValue: 85,
    medianValue: 95,
    top10Value: 130,
    gettingStartedValue: 70,
    unit: 'currency',
    higherIsBetter: true,
    insightBody:
      'You are priced below the peer median. A modest rate increase paired with a premium playing lesson can lift revenue without losing demand.',
    insightTip:
      'What top coaches do: Raise the base rate to the median and add a premium on-course playing lesson.',
    recommendedSteps: [
      {
        id: 'update-lesson-rates',
        title: 'Update your lesson rates',
        description:
          'Review and adjust pricing on your existing booking types in My Offerings.',
        icon: 'AttachMoney',
        actionPath: BENCHMARK_OFFERINGS_PATH,
      },
      {
        id: 'on-course-playing-lesson',
        title: 'Add an on-course playing lesson',
        description:
          'Create a new booking type using the On Course category for on-course instruction.',
        icon: 'GolfCourse',
        actionPath: BENCHMARK_BOOKING_TYPE_CREATE_PATH,
      },
    ],
  },
]

export const BENCHMARK_USES_SAMPLE_METRICS = true

export const BENCHMARK_SAMPLE_DATA_NOTICE =
  'Sample benchmark metrics are shown for preview. Values and insights are illustrative until your live benchmarks are available.'

export const FASTEST_WIN_BANNER = {
  badge: 'Fastest win',
  title: 'Reply to unviewed leads faster',
  body:
    'Coaches who reply within 30 minutes convert nearly 2X more leads and are growing their revenue 30% faster.',
  actionLabel: 'Review Leads',
  actionPath: BENCHMARK_LEADS_PATH,
}

export const BENCHMARK_FOOTER =
  'Benchmarks are anonymized medians across PGA Coach coaches with a comparable facility type and student volume.'

// Placeholder summary highlight until benchmark data is wired to the dashboard.
export const PLACEHOLDER_BIGGEST_UPSIDE_ID = 'first-to-second-conversion'

export const COACH_BENCHMARKS_PATH = '/pga-coach/benchmarks'
export const BENCHMARK_METRIC_QUERY_PARAM = 'metric'

export const getCoachBenchmarksPath = (metricId = null) =>
  metricId
    ? `${COACH_BENCHMARKS_PATH}?${BENCHMARK_METRIC_QUERY_PARAM}=${metricId}`
    : COACH_BENCHMARKS_PATH

export const getMetricCalculationDescription = metric =>
  metric.calculationDescription ?? metric.description

export const parseBenchmarkMetricId = search => {
  const metricId = new URLSearchParams(search).get(BENCHMARK_METRIC_QUERY_PARAM)

  if (!metricId) {
    return null
  }

  return BENCHMARK_METRICS.some(metric => metric.id === metricId)
    ? metricId
    : null
}

export const COACH_DASHBOARD_PATH = '/pga-coach'
